import PageWithSidebar from "../PageWithSidebar";
import { useContext, useEffect, useRef, useState } from "react";
import { UserInfoContext } from "../../Contexts/UserInfoContext";
import TextInput from "../Shared/TextInput";
import SaveButton from "../Shared/Buttons/SaveButton";
import { useUserApi, useTagApi } from "../../Hooks";
import InlineAlert from "../Shared/InlineAlert";
import { isNullOrWhitespace } from "../../Utils/TextUtils";
import { storageUrl, region } from "../../Configs";
import AvatarPlaceholder from "../../assets/img/avatar_placeholder.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faXmark, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import ChangePasswordButton from "../Shared/Buttons/ChangePasswordButton";
import ChangePasswordModal from "./ChangePasswordModal";
import { useTranslation } from "react-i18next";

export default function EditarCadastro() {
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({});
    const [userInfo, setUserInfo] = useContext(UserInfoContext);
    const [editingUserInfo, setEditingUserInfo] = useState({});
    const userApi = useUserApi();
    const [tempAvatarUrl, setTempAvatarUrl] = useState("");
    const [removeAvatar, setRemoveAvatar] = useState(false);
    const [newAvatarFile, setNewAvatarFile] = useState(null);
    const imageInputRef = useRef(null);
    const avatarUrl = tempAvatarUrl ? tempAvatarUrl : editingUserInfo.avatarPath ? `${storageUrl}/${editingUserInfo.avatarPath}` : AvatarPlaceholder;
    const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
    const { t } = useTranslation();
    const tagApi = useTagApi();
    const [areaOptions, setAreaOptions] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    
    const isBrazilRegion = region == "BR";

    // Carrega os dados do usuário e as tags marcadas dele
    useEffect(() => {
        setLoading(true);
        const loadData = async () => {
            await new Promise((r) => setTimeout(r, 500));
            setEditingUserInfo(userInfo);
            setLoading(false);
            if(isBrazilRegion)
                await loadUserAreas();
        };
        loadData();
    }, [userInfo]);

    const loadUserAreas = async () => {
        setLoadingAreas(true);
        tagApi.tagsByUserList()
            .then((tags) => {
                setAreaOptions(tags);
            })
            .finally(() => setLoadingAreas(false));
    }

    useEffect(() => {
        const selecionadas = areaOptions.filter(o => o.checked).map(o => o.id);
        setEditingUserInfo((prev) => ({ ...prev, areaTags: selecionadas }));
    }, [areaOptions]);

    const prepareFormForRequest = () => {
        const form = new FormData();
        form.append('id', editingUserInfo.id);
        form.append('name', editingUserInfo.name);
        form.append('email', editingUserInfo.email);
        if (removeAvatar) form.append('removeAvatar', removeAvatar);
        if (newAvatarFile) form.append('avatar', newAvatarFile);
        if (editingUserInfo.areaTags && editingUserInfo.areaTags.length > 0) {
            editingUserInfo.areaTags.forEach(id => form.append('areaTags', id));
        }
        
        return form;
    };

    const handleAvatarClick = () => imageInputRef.current.click();

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewAvatarFile(file);
            setTempAvatarUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmitUserInfo = async (e) => {
        e.preventDefault();
        if (isNullOrWhitespace(editingUserInfo.name) || isNullOrWhitespace(editingUserInfo.email)) {
            setAlert({ type: "error", message: t("nameEmailRequired") });
            return;
        }
        setLoading(true);
        setAlert({});
        await new Promise((r) => setTimeout(r, 500));
        try {
            const result = await userApi.updateUserInfo(prepareFormForRequest());
            if (result.status === 200) {
                setNewAvatarFile(null);
                setRemoveAvatar(false);
                const editedUserInfo = result.data;
                const loggedUserInfo = { ...userInfo, ...editedUserInfo };
                setUserInfo(loggedUserInfo);
                setAlert({ type: "success", message: t("updateSuccess") });
            } else {
                if (result.response.data.some(e => e === "E-mail já está sendo utilizado"))
                    setAlert({ type: "error", message: t("emailInUse") });
                else
                    setAlert({ type: "error", message: t("updateFail") });
            }
        } catch (e) {
            setAlert({ type: "error", message: t("updateFail") });
        }
        setLoading(false);
    };

    const handleRemoveAvatar = () => {
        setRemoveAvatar(true);
        setTempAvatarUrl(AvatarPlaceholder);
    };

    const handleUndoAvatar = () => {
        if (removeAvatar) setRemoveAvatar(false);
        if (newAvatarFile) setNewAvatarFile(null);
        setTempAvatarUrl("");
    };

    const RemoveAvatarButton = () => (
        <span className="ml-auto">
            <FontAwesomeIcon className="peer text-playmove-red ml-auto cursor-pointer transition-all duration-200 ease-in-out hover:scale-125" icon={faXmark} onClick={handleRemoveAvatar} />
            <FontAwesomeIcon className="absolute left-0 top-0 bottom-0 right-0 m-auto h-12 transition-all ease-in-out pointer-events-none duration-200 text-black opacity-0 can-hover:peer-hover:opacity-50 peer-hover:scale-105" icon={faXmark} />
        </span>
    );

    const UndoAvatarButton = () => (
        <span className="ml-auto">
            <FontAwesomeIcon className="peer text-playmove-red ml-auto cursor-pointer transition-all duration-200 ease-in-out hover:scale-125" icon={faRotateLeft} onClick={handleUndoAvatar} />
            <FontAwesomeIcon className="absolute left-0 top-0 bottom-0 right-0 m-auto h-12 transition-all ease-in-out pointer-events-none duration-200 text-black opacity-0 can-hover:peer-hover:opacity-50 peer-hover:scale-105" icon={faRotateLeft} />
        </span>
    );

    return (
        <PageWithSidebar loading={loading} additionalClasses="flex flex-col gap-y-8">
            <ChangePasswordModal
                email={userInfo.email}
                isOpen={changePasswordModalOpen}
                onClose={() => setChangePasswordModalOpen(false)}
                onSuccess={() => setAlert({ type: "success", message: t("passwordUpdated") })}
            />
            <div className="flex h-12 md:h-24 text-2xl border-b-gray-300 border-b-[3px]">
                <b>{t("editProfile")}</b>
            </div>

            <div className="flex flex-col gap-y-8">
                <div className="flex flex-col-reverse w-full md:flex-row gap-x-8 gap-y-3">
                    <div className="flex flex-col w-full md:w-96">
                        <InlineAlert alert={alert} visible={alert.type === "error" || alert.type === "success"} />
                        <TextInput id="nome" label={t("name")} placeholder={t("yourName")} type="text"
                            value={editingUserInfo.name}
                            onChangeText={(e) => setEditingUserInfo({ ...editingUserInfo, name: e.target.value })}
                            onClick={() => setAlert({})}
                            error={alert.type === "error"} size="sm" />

                        <TextInput id="email" label={t("email")} placeholder={t("yourEmail")} type="text"
                            value={editingUserInfo.email}
                            onChangeText={(e) => setEditingUserInfo({ ...editingUserInfo, email: e.target.value })}
                            onClick={() => setAlert({})}
                            error={alert.type === "error"} className="mt-5" size="sm" />

                    {isBrazilRegion && (
                        <div className="mt-6">
                            <b>Área de atuação</b>
                            <div className="flex flex-col mt-2 gap-2">
                                {loadingAreas ? (
                                    <span>Carregando áreas...</span>
                                ) : (
                                    areaOptions.map((opt, i) => (
                                        <label key={opt.id} className="flex items-center gap-2 text-base">
                                            <input
                                                type="checkbox"
                                                checked={!!opt.checked}
                                                onChange={() => {
                                                    setAreaOptions((prev) =>
                                                        prev.map((item, idx) =>
                                                            idx === i ? { ...item, checked: !item.checked } : item
                                                        )
                                                    );
                                                }}
                                                className="w-5 h-5 accent-blue-500 rounded border-gray-300"
                                            />
                                            {opt.name}
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    </div>

                    <div className="flex flex-col rounded-lg h-56 py-6 w-full md:w-56 bg-playmove-lightblue">
                        <div className="relative h-32 w-32 flex self-center">
                            <input type="file" ref={imageInputRef} className="hidden" onChange={handleAvatarChange} accept=".png, .jpeg, .jpg" />
                            <div className="peer absolute h-full w-full group rounded-full cursor-pointer bg-cover transition-all duration-200 ease-in-out hover:scale-105" onClick={handleAvatarClick} style={{ backgroundImage: `url(${avatarUrl})` }}></div>
                            <FontAwesomeIcon className="absolute left-0 top-0 bottom-0 right-0 m-auto h-12 transition-all ease-in-out pointer-events-none duration-200 text-black opacity-0 can-hover:peer-hover:opacity-50 peer-hover:scale-105" icon={faPencil} />
                            {removeAvatar || newAvatarFile ? UndoAvatarButton() : RemoveAvatarButton()}
                        </div>
                        <p className="text-center font-bold mt-auto">{t("clickToEditPhoto")}</p>
                    </div>
                </div>

                <div className="flex flex-row gap-x-3">
                    <ChangePasswordButton onClick={() => setChangePasswordModalOpen(true)} />
                    <SaveButton onClick={handleSubmitUserInfo} />
                </div>
            </div>
        </PageWithSidebar>
    );
}
